import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, X, ChevronRight, Rocket } from "lucide-react";
import { useTranslation } from "@/i18n/translations";

export default function OnboardingGuide({ onNavigate }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    axios.get(`${API}/onboarding/status`).then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  if (!status || status.dismissed || status.done) return null;

  const steps = [
    { key: "step1", tab: "settings", title: t("onboarding.step1"), desc: t("onboarding.step1Desc") },
    { key: "step2", tab: "widget", title: t("onboarding.step2"), desc: t("onboarding.step2Desc") },
    { key: "step3", tab: "subscription", title: t("onboarding.step3"), desc: t("onboarding.step3Desc") },
    { key: "step4", tab: "widget", title: t("onboarding.step4"), desc: t("onboarding.step4Desc") },
  ];
  const doneCount = steps.filter((s) => status[s.key]).length;

  const dismiss = async () => {
    setStatus({ ...status, dismissed: true });
    try { await axios.post(`${API}/onboarding/dismiss`); } catch (e) {}
  };

  return (
    <Card className="bg-white border-4 border-black mb-4 md:mb-6" data-testid="onboarding-guide">
      <CardContent className="pt-4 md:pt-6 pb-4 md:pb-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-black text-white rounded-lg p-2 hidden sm:block">
              <Rocket className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-bold text-black" data-testid="onboarding-title">
                🚀 {t("onboarding.title")}{" "}
                <span className="text-gray-500 font-semibold text-base" data-testid="onboarding-progress">
                  [{doneCount}/4]
                </span>
              </h3>
              <p className="text-xs md:text-sm text-gray-600">{t("onboarding.subtitle")}</p>
            </div>
          </div>
          <button
            data-testid="onboarding-dismiss-btn"
            onClick={dismiss}
            title={t("onboarding.dismiss")}
            className="text-gray-400 hover:text-black transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2 border border-black mb-4">
          <div
            className="h-full rounded-full bg-green-500 transition-all"
            style={{ width: `${(doneCount / 4) * 100}%` }}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-3">
          {steps.map((s, i) => {
            const isDone = !!status[s.key];
            return (
              <button
                key={s.key}
                data-testid={`onboarding-${s.key}`}
                onClick={() => !isDone && onNavigate && onNavigate(s.tab)}
                className={`text-left rounded-lg border-2 p-3 transition-colors ${
                  isDone
                    ? "border-green-500 bg-green-50 cursor-default"
                    : "border-gray-300 bg-white hover:border-black cursor-pointer"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {isDone ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                  ) : (
                    <span className="w-5 h-5 rounded-full border-2 border-black text-black text-xs font-bold flex items-center justify-center shrink-0">
                      {i + 1}
                    </span>
                  )}
                  <span className={`text-sm font-bold ${isDone ? "text-green-700 line-through" : "text-black"}`}>
                    {s.title}
                  </span>
                  {!isDone && <ChevronRight className="w-4 h-4 text-gray-400 ml-auto shrink-0" />}
                </div>
                <p className={`text-xs ${isDone ? "text-green-600" : "text-gray-600"}`}>{s.desc}</p>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
